import { istek } from './istemci';
import type { Semalar } from './sema';
import type { Kimlik } from './tipler';

/**
 * KULLANICI HESABI UÇLARI (9).
 *
 * HESAP PERSONELDEN AYRI BİR VARLIKTIR. Her personelin hesabı olmak zorunda
 * değil: saha personelinin bir bölümü puantörle takip ediliyor ve hiç giriş
 * yapmıyor. Bu yüzden `hesapsiz-personel` ayrı bir uç — hesap açılacakları
 * bulmak için personel listesini elemek gerekmesin.
 *
 * YETKİ İKİ SEVİYEDE:
 *   GET · POST · şifre sıfırla · kilit aç   SISTEM_YONET | PERSONEL_YONET
 *   PATCH durum · POST rol · rol sonlandır  SISTEM_YONET               ← TEK
 *
 * Ayrım bilinçli: İK hesap açar ve şifre sıfırlar (günlük iş), ama hesabı
 * kapatmak ve ROL VERMEK yalnız sistem yöneticisinde. Rol yetki demek;
 * personel işlemleri yapan biri kendine yetki dağıtamamalı.
 */

export type KullaniciOzeti = Semalar['KullaniciOzetiDto'];
export type KullaniciSayfasi = Semalar['KullaniciSayfasiDto'];
export type HesapsizPersonel = Semalar['HesapsizPersonelDto'];
export type GeciciSifre = Semalar['GeciciSifreDto'];

/**
 * ROL KATALOĞU İÇİN UÇ YOK — kodlar seed'de sabit (`11_sistem.sql`).
 *
 * `izin_tipi`nin aksine bunlar `serial` DEĞİL, doğal anahtar (`kod`) ile
 * çalışıyor ve `sistem_mi = true` işaretli; yani panelden değiştirilemezler.
 * Bu yüzden sabit yazmak güvenli. Yeni rol eklenirse burası da güncellenmeli
 * (`IS_LISTESI.md` §3'e yazıldı).
 */
export const ROLLER = [
  {
    kod: 'SISTEM_ADMIN',
    ad: 'Sistem Yöneticisi',
    aciklama: 'Kurumsal yapı, müdürlük açma/kapatma, kullanıcı yönetimi. Pozisyondan bağımsız.',
  },
  {
    kod: 'IK_YETKILI',
    ad: 'İnsan Kaynakları Yetkilisi',
    aciklama: 'Kurum geneli personel, ücret, dönem kilitleme ve parametre yönetimi.',
  },
  {
    kod: 'MALI_YETKILI',
    ad: 'Mali Hizmetler Yetkilisi',
    aciklama: 'Yalnız toplu gösterge: saat ve tutar. Kişi bazlı detay GÖRMEZ.',
  },
  {
    kod: 'DENETCI',
    ad: 'Denetçi / Teftiş',
    aciklama: 'Kurum geneli okuma. Her erişim denetim izine yazılır.',
  },
  {
    kod: 'SALT_OKUR',
    ad: 'Salt Okuyucu',
    aciklama: 'Yalnız görüntüleme; hiçbir değişiklik yapamaz.',
  },
] as const;

export interface KullaniciSorgusu {
  arama?: string;
  aktif?: boolean;
  kilitliMi?: boolean;
  /** Şifresini hiç değiştirmemiş hesaplar — ilk giriş yapmamış olanlar. */
  sifreDegistirmemis?: boolean;
  birimId?: Kimlik;
  sayfa?: number;
  sayfaBoyutu?: number;
}

/* ------------------------------------------------------------------ */

export function kullaniciListesi(sorgu: KullaniciSorgusu = {}): Promise<KullaniciSayfasi> {
  return istek<KullaniciSayfasi>('/v1/kullanici', { sorgu: { ...sorgu } });
}

/** Hesabı olmayan personel — hesap açma ekranının kaynağı. */
export function hesapsizPersonel(): Promise<HesapsizPersonel[]> {
  return istek<HesapsizPersonel[]>('/v1/kullanici/hesapsiz-personel');
}

export function kullaniciAyrintisi(id: Kimlik): Promise<KullaniciOzeti> {
  return istek<KullaniciOzeti>(`/v1/kullanici/${id}`);
}

/**
 * Hesap açar. GÖVDE YALNIZ `personelId` KABUL EDİYOR.
 *
 * Kullanıcı adı personel numarasından türetiliyor; panelden verilseydi iki
 * kişi aynı adı alabilir ve giriş belirsizleşirdi. Geçici şifre yanıtta BİR
 * KEZ döner ve hiçbir yerde saklanmaz — kaybolursa sıfırlanır.
 */
export function kullaniciOlustur(personelId: Kimlik): Promise<GeciciSifre> {
  return istek<GeciciSifre>('/v1/kullanici', { yontem: 'POST', govde: { personelId } });
}

/** Yeni geçici şifre üretir. İlk girişte değiştirme ZORUNLU olur. */
export function sifreSifirla(id: Kimlik, gerekce?: string): Promise<GeciciSifre> {
  return istek<GeciciSifre>(`/v1/kullanici/${id}/sifre-sifirla`, {
    yontem: 'POST',
    govde: { gerekce },
  });
}

/**
 * Hatalı giriş kilidini açar.
 *
 * Kilit SİLİNMEZ, açılır: kaç kez kilitlendiği denetim izinde kalır. Sürekli
 * kilitlenen bir hesap ya şifresini unutmuş bir kullanıcı ya da deneme
 * saldırısıdır; ikisi de izlenebilir olmalı.
 */
export function kilitAc(id: Kimlik): Promise<void> {
  // GÖVDE ALMIYOR. Gerekçe alanı yok: kilidi açmanın tek sebebi var ve
  // işlemi yapan kullanıcı denetim izine zaten yazılıyor.
  return istek<void>(`/v1/kullanici/${id}/kilit-ac`, { yontem: 'POST' });
}

/** Hesap SİLİNMEZ, pasife alınır. Yalnız `SISTEM_YONET`. */
export function durumDegistir(id: Kimlik, aktif: boolean): Promise<void> {
  // `DurumDegistirDto` YALNIZ `aktif` kabul ediyor; `gerekce` gönderilirse
  // `forbidNonWhitelisted` ile 400 döner.
  return istek<void>(`/v1/kullanici/${id}/durum`, { yontem: 'PATCH', govde: { aktif } });
}

/**
 * Rol atar. Yalnız `SISTEM_YONET`.
 *
 * `gecerlilikBitis` verilirse rol SÜRELİ olur — vekâlet ve geçici görev
 * için. Süresiz rol vermek varsayılan değil ama mümkün; bitiş boş bırakılır.
 */
export function rolAta(
  id: Kimlik,
  girdi: {
    rolKod: string;
    gecerlilikBaslangic?: string;
    gecerlilikBitis?: string;
    dayanakNo?: string;
  },
): Promise<void> {
  return istek<void>(`/v1/kullanici/${id}/rol`, { yontem: 'POST', govde: girdi });
}

/** Rolü sonlandırır — kayıt silinmez, bitiş tarihi yazılır. */
export function rolSonlandir(id: Kimlik, rolKod: string): Promise<void> {
  // GÖVDE ALMIYOR — rol kaydı silinmez, bitiş tarihi yazılır ve işlemi yapan
  // denetim izinde kalır.
  return istek<void>(`/v1/kullanici/${id}/rol/${rolKod}/sonlandir`, { yontem: 'PATCH' });
}
